/**
 * Production Monitoring Dashboard
 *
 * Real-time monitoring of backend circuit breakers, authentication, and safety metrics
 * Integrates with new backend production readiness features
 */

import React, { useState, useEffect } from 'react';
import { Card, Badge, Grid, Text, Group, Stack, Progress, Alert } from '@mantine/core';
import { IconAlertTriangle, IconShieldCheck, IconCircuitBattery, IconGauge } from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

// GraphQL query for backend metrics
const BACKEND_METRICS_QUERY = gql`
  query BackendMetrics {
    systemMetrics {
      circuitBreakers {
        name
        state
        failureRate
        totalRequests
        failedRequests
        lastFailureTime
      }
      authentication {
        provider
        activeTokens
        refreshRate
        failureRate
      }
      safetyScore
      lifeCriticalMetrics {
        patientSafetyScore
        financialIntegrityScore
        legalComplianceScore
        emergencyControlsActive
      }
      loadTesting {
        lastRunTime
        averageResponseTime
        throughput
        errorRate
        status
      }
    }
  }
`;

interface CircuitBreakerMetrics {
  name: string;
  state: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
  failureRate: number;
  totalRequests: number;
  failedRequests: number;
  lastFailureTime?: string;
}

interface AuthMetrics {
  provider: string;
  activeTokens: number;
  refreshRate: number;
  failureRate: number;
}

interface LifeCriticalMetrics {
  patientSafetyScore: number;
  financialIntegrityScore: number;
  legalComplianceScore: number;
  emergencyControlsActive: boolean;
}

interface LoadTestMetrics {
  lastRunTime: string;
  averageResponseTime: number;
  throughput: number;
  errorRate: number;
  status: 'PASS' | 'FAIL' | 'RUNNING';
}

interface SystemMetrics {
  circuitBreakers: CircuitBreakerMetrics[];
  authentication: AuthMetrics;
  safetyScore: number;
  lifeCriticalMetrics: LifeCriticalMetrics;
  loadTesting: LoadTestMetrics;
}

export const ProductionMonitoringDashboard: React.FC = () => {
  const { data, loading, error, refetch } = useQuery<Record<string, any>>(BACKEND_METRICS_QUERY, {
    pollInterval: 10000, // Poll every 10 seconds
    errorPolicy: 'all'
  });

  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    if (data?.systemMetrics) {
      const metrics = data?.systemMetrics as SystemMetrics;

      // Count critical alerts
      let alerts = 0;

      // Circuit breaker alerts
      metrics.circuitBreakers.forEach(cb => {
        if (cb.state === 'OPEN') alerts++;
        if (cb.failureRate > 5) alerts++;
      });

      // Safety score alerts
      if (metrics.safetyScore < 85) alerts++;

      // Load testing alerts
      if (metrics.loadTesting.status === 'FAIL') alerts++;
      if (metrics.loadTesting.errorRate > 1) alerts++;

      setAlertCount(alerts);
    }
  }, [data]);

  if (loading) {
    return <Text>Loading production metrics...</Text>;
  }

  if (error) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} title="Metrics Error" color="red">
        Failed to load production metrics: {error.message}
      </Alert>
    );
  }

  const metrics = data?.systemMetrics as SystemMetrics;

  if (!metrics) {
    return <Text>No metrics available</Text>;
  }

  const getCircuitBreakerColor = (state: string): string => {
    switch (state) {
      case 'CLOSED': return 'green';
      case 'OPEN': return 'red';
      case 'HALF_OPEN': return 'yellow';
      default: return 'gray';
    }
  };

  const getSafetyScoreColor = (score: number): string => {
    if (score >= 95) return 'green';
    if (score >= 85) return 'yellow';
    return 'red';
  };

  return (
    <Stack gap="lg">
      {/* Header with Overall Status */}
      <Group justify="space-between">
        <Text size="xl" fw={700}>Production Monitoring Dashboard</Text>
        {alertCount > 0 && (
          <Badge color="red" size="lg">
            {alertCount} Critical Alert{alertCount !== 1 ? 's' : ''}
          </Badge>
        )}
      </Group>

      <Grid>
        {/* Circuit Breakers */}
        <Grid.Col span={6}>
          <Card withBorder>
            <Group justify="space-between" mb="md">
              <Text fw={600}>Circuit Breakers</Text>
              <IconCircuitBattery size={20} />
            </Group>

            <Stack gap="sm">
              {metrics.circuitBreakers.map((cb, index) => (
                <Group key={index} justify="space-between">
                  <Text size="sm">{cb.name}</Text>
                  <Group gap="xs">
                    <Badge color={getCircuitBreakerColor(cb.state)} size="sm">
                      {cb.state}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {cb.failureRate.toFixed(1)}% failures
                    </Text>
                  </Group>
                </Group>
              ))}
            </Stack>
          </Card>
        </Grid.Col>

        {/* Authentication Status */}
        <Grid.Col span={6}>
          <Card withBorder>
            <Group justify="space-between" mb="md">
              <Text fw={600}>Authentication</Text>
              <IconShieldCheck size={20} />
            </Group>

            <Stack gap="sm">
              <Group justify="space-between">
                <Text size="sm">Provider</Text>
                <Badge variant="outline">{metrics.authentication.provider}</Badge>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Active Tokens</Text>
                <Text size="sm" fw={600}>{metrics.authentication.activeTokens}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Auth Failure Rate</Text>
                <Text size="sm" c={metrics.authentication.failureRate > 1 ? 'red' : 'green'}>
                  {metrics.authentication.failureRate.toFixed(2)}%
                </Text>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Safety Score */}
        <Grid.Col span={12}>
          <Card withBorder>
            <Group justify="space-between" mb="md">
              <Text fw={600}>Life-Critical Safety Score</Text>
              <Badge
                color={getSafetyScoreColor(metrics.safetyScore)}
                size="lg"
                variant="filled"
              >
                {metrics.safetyScore}%
              </Badge>
            </Group>

            <Progress
              value={metrics.safetyScore}
              color={getSafetyScoreColor(metrics.safetyScore)}
              size="xl"
              mb="md"
            />

            <Grid>
              <Grid.Col span={3}>
                <Stack gap="xs">
                  <Text size="xs" c="dimmed">Patient Safety</Text>
                  <Text fw={600} c={metrics.lifeCriticalMetrics.patientSafetyScore >= 85 ? 'green' : 'red'}>
                    {metrics.lifeCriticalMetrics.patientSafetyScore}%
                  </Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={3}>
                <Stack gap="xs">
                  <Text size="xs" c="dimmed">Financial Integrity</Text>
                  <Text fw={600} c={metrics.lifeCriticalMetrics.financialIntegrityScore >= 85 ? 'green' : 'red'}>
                    {metrics.lifeCriticalMetrics.financialIntegrityScore}%
                  </Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={3}>
                <Stack gap="xs">
                  <Text size="xs" c="dimmed">Legal Compliance</Text>
                  <Text fw={600} c={metrics.lifeCriticalMetrics.legalComplianceScore >= 85 ? 'green' : 'red'}>
                    {metrics.lifeCriticalMetrics.legalComplianceScore}%
                  </Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={3}>
                <Stack gap="xs">
                  <Text size="xs" c="dimmed">Emergency Controls</Text>
                  <Badge
                    color={metrics.lifeCriticalMetrics.emergencyControlsActive ? 'green' : 'red'}
                    variant="filled"
                  >
                    {metrics.lifeCriticalMetrics.emergencyControlsActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Stack>
              </Grid.Col>
            </Grid>
          </Card>
        </Grid.Col>

        {/* Load Testing Results */}
        <Grid.Col span={12}>
          <Card withBorder>
            <Group justify="space-between" mb="md">
              <Text fw={600}>Load Testing Status</Text>
              <Badge
                color={metrics.loadTesting.status === 'PASS' ? 'green' :
                       metrics.loadTesting.status === 'FAIL' ? 'red' : 'blue'}
              >
                {metrics.loadTesting.status}
              </Badge>
            </Group>

            <Grid>
              <Grid.Col span={3}>
                <Stack gap="xs">
                  <Text size="xs" c="dimmed">Avg Response Time</Text>
                  <Text fw={600} c={metrics.loadTesting.averageResponseTime > 500 ? 'red' : 'green'}>
                    {metrics.loadTesting.averageResponseTime}ms
                  </Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={3}>
                <Stack gap="xs">
                  <Text size="xs" c="dimmed">Throughput</Text>
                  <Text fw={600} c={metrics.loadTesting.throughput < 25 ? 'red' : 'green'}>
                    {metrics.loadTesting.throughput} RPS
                  </Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={3}>
                <Stack gap="xs">
                  <Text size="xs" c="dimmed">Error Rate</Text>
                  <Text fw={600} c={metrics.loadTesting.errorRate > 1 ? 'red' : 'green'}>
                    {metrics.loadTesting.errorRate.toFixed(2)}%
                  </Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={3}>
                <Stack gap="xs">
                  <Text size="xs" c="dimmed">Last Run</Text>
                  <Text size="sm">
                    {new Date(metrics.loadTesting.lastRunTime).toLocaleString()}
                  </Text>
                </Stack>
              </Grid.Col>
            </Grid>

            {metrics.loadTesting.status === 'FAIL' && (
              <Alert mt="md" icon={<IconAlertTriangle size={16} />} color="red">
                Load testing failed! Check error rate and response times.
              </Alert>
            )}
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default ProductionMonitoringDashboard;